import EditablePropsType from '../../../types/composer';
import { fontStyleSchema } from './font';

/** Composer block name for the global answers value-prop block. */
export const ANSWERS_VALUE_PROP_BLOCK_NAME = 'answers-value-prop';

/**
 * Font-size options for the value-prop text fields. Plain `text-*` classes:
 * every consumer merges them via `cn` (twMerge), which resolves conflicting
 * text-size classes in favor of the composer-selected value, so no `!important`
 * is needed. An empty value means "use the element's default size".
 */
export const FONT_SIZE_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Extra small', value: 'text-xs' },
  { label: 'Small', value: 'text-sm' },
  { label: 'Medium', value: 'text-base' },
  { label: 'Large', value: 'text-lg' },
  { label: 'Extra large', value: 'text-xl' },
  { label: '2X large', value: 'text-2xl' },
  { label: '3X large', value: 'text-3xl' },
];

/**
 * Quote-mark color options for testimonials. Values are `text-*` Tailwind
 * classes backed by the `wp-*` color tokens in the workspace preset. An empty
 * value means "use the default color for this testimonial's position".
 */
export const QUOTE_COLOR_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Green', value: 'text-wp-green' },
  { label: 'Red', value: 'text-wp-red' },
  { label: 'Blue', value: 'text-wp-action-blue' },
];

/** Default quote-mark color per testimonial position (cycles). */
export const DEFAULT_QUOTE_COLORS = [
  'text-wp-green',
  'text-wp-red',
  'text-wp-action-blue',
];

/**
 * Builds a font-size Select schema for a text field. `defaultValue` should
 * mirror the component's base size class so the composer pre-fills with the
 * size actually rendered.
 */
const fontSize = (name: string, label: string, defaultValue = '') => ({
  type: EditablePropsType.Select,
  name,
  label,
  selectOptions: { options: FONT_SIZE_OPTIONS },
  defaultValue,
});

/**
 * Reserved slug of the single composer page that holds the global, editable
 * value-prop copy shared by every answer page. `/__config/*` slugs are kept out
 * of public routing (see lib/backend/composer/serverPage.ts).
 */
export const ANSWERS_VALUE_PROP_SLUG = '/__config/answers-value-prop';

export const DEFAULT_TRIAL_CTA = {
  buttonLabel: 'Try Osmosis Free',
  fineprint: 'No credit card required',
};

// Default copy used to (a) pre-fill a newly created config page and (b) as the
// components' fallback when no config exists. Single source of truth.
export const DEFAULT_TRIAL_FEATURES = [
  '1,700+ whiteboard-style videos',
  '2,700+ board-style questions',
  'Spaced-repetition flashcards',
  'Mobile app — study anywhere',
];

export const DEFAULT_SOCIAL_PROOF_HEADING =
  'Students say Osmosis is 100% worth it';

export const DEFAULT_SOCIAL_PROOF_SUBTEXT =
  'Because Osmosis saves them time. Lowers stress. And actually helps them remember when it counts.';

export const DEFAULT_SOCIAL_PROOF_TESTIMONIALS: SocialProofTestimonial[] = [
  {
    quote:
      'I used Osmosis to prepare for my first medical school licensing exam! Super helpful and interactive for people who may not do great with just pages of text info!',
    name: 'Cecilia Ruiz',
    role: 'MD student',
    avatar: '/images/testimonials/cecilia-ruiz.png',
    quoteColor: 'text-wp-green',
  },
  {
    quote:
      'I have used Osmosis for about four years. Best thing I have ever used for my medical studies.',
    name: 'Sayan Misra',
    role: 'Med student',
    avatar: '/images/testimonials/sayan-misra.png',
    image: '/images/testimonials/sayan-misra-hero.jpg',
    quoteColor: 'text-wp-red',
  },
  {
    quote:
      'Osmosis videos are superior because they define simple concepts, tell a story with a clear progression, and provide context.',
    name: 'Jay Pate',
    role: 'Dental student',
    avatar: '/images/testimonials/jay-pate.png',
    quoteColor: 'text-wp-action-blue',
  },
];

/**
 * Schema for the global "Answers Value Prop" composer block. A single config
 * page holds one of these blocks; every answer page reads it and threads the
 * copy into AnswerBodyCTA / AnswerTrialCard / AnswerSocialProof. Defaults mirror
 * the components' current hardcoded copy so the composer pre-fills with today's
 * text and the answer page falls back to it when the config is absent.
 */

// ---- Shared trial CTA (all logged-out Answer-page signup surfaces) ----
export const trialCtaSchema = {
  type: EditablePropsType.Object,
  name: 'trialCta',
  label: 'Trial CTA',
  properties: {
    buttonLabel: {
      type: EditablePropsType.Text,
      name: 'buttonLabel',
      label: 'Button label',
      defaultValue: DEFAULT_TRIAL_CTA.buttonLabel,
    },
    fineprint: {
      type: EditablePropsType.Text,
      name: 'fineprint',
      label: 'Fineprint',
      defaultValue: DEFAULT_TRIAL_CTA.fineprint,
    },
  },
};

// ---- Body CTA (in-article, below the first image) ----
export const bodyCtaSchema = {
  type: EditablePropsType.Object,
  name: 'bodyCta',
  label: 'Body CTA',
  properties: {
    eyebrow: {
      type: EditablePropsType.Text,
      name: 'eyebrow',
      label: 'Eyebrow',
      defaultValue: 'Learn deeper with Osmosis',
    },
    eyebrowFontSize: fontSize(
      'eyebrowFontSize',
      'Eyebrow font size',
      'text-xs',
    ),
    headline: {
      type: EditablePropsType.Text,
      name: 'headline',
      label: 'Headline',
      defaultValue: 'Master this topic faster with videos, questions, and AI.',
    },
    headlineFontSize: fontSize(
      'headlineFontSize',
      'Headline font size',
      'text-base',
    ),
    reassurance: {
      type: EditablePropsType.Text,
      name: 'reassurance',
      label: 'Reassurance',
      defaultValue: 'Used by 8M+ healthcare learners.',
    },
    reassuranceFontSize: fontSize(
      'reassuranceFontSize',
      'Reassurance font size',
      'text-sm',
    ),
    fontStyle: fontStyleSchema,
  },
};

// ---- Trial card (sidebar, above related topics) ----
export const trialFeatureSchema = {
  type: EditablePropsType.Object,
  name: 'feature',
  label: 'Feature',
  properties: {
    text: {
      type: EditablePropsType.Text,
      name: 'text',
      label: 'Feature',
    },
  },
};

export const trialCardSchema = {
  type: EditablePropsType.Object,
  name: 'trialCard',
  label: 'Trial Card',
  properties: {
    badge: {
      type: EditablePropsType.Text,
      name: 'badge',
      label: 'Badge',
      defaultValue: '7-day free trial',
    },
    badgeFontSize: fontSize('badgeFontSize', 'Badge font size', 'text-xs'),
    heading: {
      type: EditablePropsType.Text,
      name: 'heading',
      label: 'Heading',
      defaultValue: 'Go deeper with Osmosis',
    },
    headingFontSize: fontSize(
      'headingFontSize',
      'Heading font size',
      'text-base',
    ),
    description: {
      type: EditablePropsType.Text,
      name: 'description',
      label: 'Description',
      defaultValue:
        'Videos, flashcards & practice questions built for healthcare learners.',
    },
    descriptionFontSize: fontSize(
      'descriptionFontSize',
      'Description font size',
      'text-sm',
    ),
    ratingText: {
      type: EditablePropsType.Text,
      name: 'ratingText',
      label: 'Star rating text',
      defaultValue: '4.8 · 12,000+ reviews',
    },
    ratingTextFontSize: fontSize(
      'ratingTextFontSize',
      'Rating font size',
      'text-sm',
    ),
    features: {
      type: EditablePropsType.Array,
      name: 'features',
      label: 'Features',
      items: trialFeatureSchema,
    },
    featuresFontSize: fontSize(
      'featuresFontSize',
      'Features font size',
      'text-xs',
    ),
    fontStyle: fontStyleSchema,
  },
};

// ---- Social proof (sidebar, below related topics) ----
export const testimonialSchema = {
  type: EditablePropsType.Object,
  name: 'testimonial',
  label: 'Testimonial',
  properties: {
    quote: {
      type: EditablePropsType.Text,
      name: 'quote',
      label: 'Quote',
    },
    name: {
      type: EditablePropsType.Text,
      name: 'name',
      label: 'Name',
    },
    role: {
      type: EditablePropsType.Text,
      name: 'role',
      label: 'Role',
    },
    quoteColor: {
      type: EditablePropsType.Select,
      name: 'quoteColor',
      label: 'Quote mark color',
      selectOptions: { options: QUOTE_COLOR_OPTIONS },
      defaultValue: '',
    },
    avatar: {
      type: EditablePropsType.Media,
      name: 'avatar',
      label: 'Avatar (optional)',
      accept: ['image/*'],
    },
    image: {
      type: EditablePropsType.Media,
      name: 'image',
      label: 'Hero image (optional)',
      accept: ['image/*'],
    },
  },
};

export const socialProofSchema = {
  type: EditablePropsType.Object,
  name: 'socialProof',
  label: 'Social Proof',
  properties: {
    heading: {
      type: EditablePropsType.Text,
      name: 'heading',
      label: 'Heading',
      defaultValue: DEFAULT_SOCIAL_PROOF_HEADING,
    },
    headingFontSize: fontSize(
      'headingFontSize',
      'Heading font size',
      'text-3xl',
    ),
    subtext: {
      type: EditablePropsType.Text,
      name: 'subtext',
      label: 'Subtext',
      defaultValue: DEFAULT_SOCIAL_PROOF_SUBTEXT,
    },
    subtextFontSize: fontSize(
      'subtextFontSize',
      'Subtext font size',
      'text-lg',
    ),
    testimonials: {
      type: EditablePropsType.Array,
      name: 'testimonials',
      label: 'Testimonials',
      items: testimonialSchema,
    },
    // Testimonial quote size is fixed at 20px (text-xl) per the Figma spec, so
    // there is no composer control for it (a stored value would override it).
    // Social proof renders in National 2 by default (see AnswerSocialProof).
    fontStyle: { ...fontStyleSchema, defaultValue: 'font-national-2' },
  },
};

export type BodyCtaArgs = {
  eyebrow?: string;
  eyebrowFontSize?: string;
  headline?: string;
  headlineFontSize?: string;
  reassurance?: string;
  reassuranceFontSize?: string;
  buttonLabel?: string;
  buttonLabelFontSize?: string;
  fineprint?: string;
  fineprintFontSize?: string;
  fontStyle?: string;
};

export type TrialCtaArgs = {
  buttonLabel?: string;
  fineprint?: string;
};

export type TrialCardArgs = {
  badge?: string;
  badgeFontSize?: string;
  heading?: string;
  headingFontSize?: string;
  description?: string;
  descriptionFontSize?: string;
  ratingText?: string;
  ratingTextFontSize?: string;
  features?: { text: string }[];
  featuresFontSize?: string;
  buttonLabel?: string;
  buttonLabelFontSize?: string;
  fineprint?: string;
  fineprintFontSize?: string;
  fontStyle?: string;
};

export type SocialProofTestimonial = {
  quote: string;
  name: string;
  role: string;
  image?: string;
  /** Avatar image shown next to the name (defaults to a placeholder circle). */
  avatar?: string;
  /** `text-*` class for the quote mark; empty falls back to the position default. */
  quoteColor?: string;
};

export type SocialProofArgs = {
  heading?: string;
  headingFontSize?: string;
  subtext?: string;
  subtextFontSize?: string;
  testimonials?: SocialProofTestimonial[];
  fontStyle?: string;
};

export type AnswersValuePropArgs = {
  trialCta?: TrialCtaArgs;
  bodyCta?: BodyCtaArgs;
  trialCard?: TrialCardArgs;
  socialProof?: SocialProofArgs;
};
